import React, { useContext, useEffect, useState } from "react";
import { Table, DatePicker, Select, Button, Modal, Input } from "antd";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function MyRfc() {
    const { authData, backUrl } = useContext(AuthContext);

    const [data, setData] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filters, setFilters] = useState({
        status: null,
        month: null,
        year: null,
        fromDate: null,
        toDate: null,
    });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ✅ Fetch RFC data
    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/hr/attendance-requests/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });
            setData(res.data.results || []);
            console.log("RFC's : ", res.data.results);
        } catch (error) {
            console.error("Error fetching RFC data:", error.response || error.message);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ✅ Status colors
    const statusColors = {
        "Pending": "orange",
        "Approved": "green",
        "Rejected": "red",
    };

    // ✅ Columns
    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
        },
        {
            title: "Date",
            dataIndex: "attendance_date",
            key: "attendance_date",
            render: (date) => dayjs(date).format("DD/MM/YYYY"),
        },
        {
            title: "Original Status",
            dataIndex: "original_status_message",
            key: "original_status_message",
            render: (text) => text || "-",
        },
        {
            title: "Permission Hours Deducted",
            dataIndex: "permission_hours_deducted",
            key: "permission_hours_deducted",
            render: (hours) => `${hours} hrs`,
        },
        {
            title: "Reason",
            key: "remarks",
            width: 400,
            render: (_, record) => (
                <div
                    style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        maxWidth: "400px",
                    }}
                >
                    {record.remarks || "-"}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <span
                    style={{
                        fontWeight: 500,
                        color:
                            text === "Approved"
                                ? "green"
                                : text === "Rejected"
                                    ? "red"
                                    : "#faad14", // Pending or others
                    }}
                >
                    {text}
                </span>
            ),
        },
        {
            title: "Approvals",
            key: "approvals",
            width: 400,
            render: (_, record) => {
                const approvals = record.approvals || [];
                if (!approvals.length) return "-";

                return (
                    <div
                        style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            maxWidth: "400px",
                        }}
                    >
                        {approvals.map((a, index) => (
                            <div
                                key={index}
                                style={{
                                    borderBottom:
                                        index < approvals.length - 1 ? "1px solid #ddd" : "none",
                                    paddingBottom: "6px",
                                    marginBottom: "6px",
                                }}
                            >
                                <strong>{a.approver_name}:</strong>{" "}
                                <span
                                    style={{
                                        color:
                                            a.status === "Approved"
                                                ? "green"
                                                : a.status === "Rejected"
                                                    ? "red"
                                                    : "#faad14",
                                        fontWeight: 500,
                                    }}
                                >
                                    {a.status}
                                </span>
                                <br />
                                {a.remarks && (
                                    <>
                                        <span style={{ color: "#666" }}>
                                            Remarks: {a.remarks}
                                        </span>
                                        <br />
                                    </>
                                )}
                                {a.approved_on && (
                                    <small style={{ color: "#999" }}>
                                        {dayjs(a.approved_on).format("DD/MM/YYYY hh:mm A")}
                                    </small>
                                )}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => dayjs(date).format("DD/MM/YYYY hh:mm A"),
        },
    ];

    // ✅ FILTER LOGIC
    const filteredData = data.filter((item) => {
        const attendanceDate = dayjs(item.attendance_date);

        const matchStatus = filters.status
            ? item.status === filters.status ||
            item.approvals?.some((a) => a.status === filters.status)
            : true;
        const matchMonth = filters.month
            ? attendanceDate.month() + 1 === filters.month
            : true;
        const matchYear = filters.year
            ? attendanceDate.year() === filters.year
            : true;
        const matchDateRange =
            filters.fromDate && filters.toDate
                ? attendanceDate.isBetween(filters.fromDate, filters.toDate, "day", "[]")
                : filters.fromDate
                    ? attendanceDate.isSameOrAfter(filters.fromDate, "day")
                    : filters.toDate
                        ? attendanceDate.isSameOrBefore(filters.toDate, "day")
                        : true;

        return matchStatus && matchMonth && matchYear && matchDateRange;
    });

    // ✅ Extract unique years from data
    const years = Array.from(
        new Set(data.map((item) => dayjs(item.attendance_date).year()))
    ).sort((a, b) => b - a);

    return (
        <>
            {/* ✅ Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "16px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                <Select
                    placeholder="Filter by Year"
                    style={{ width: isMobile ? "100%" : 140 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, year: value })}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Filter by Month"
                    style={{ width: isMobile ? "100%" : 160 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, month: value })}
                >
                    {dayjs.months().map((month, index) => (
                        <Select.Option key={index} value={index + 1}>
                            {month}
                        </Select.Option>
                    ))}
                </Select>

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters({ ...filters, fromDate: date })}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters({ ...filters, toDate: date })}
                />

                <Select
                    placeholder="Status"
                    style={{ width: isMobile ? "100%" : 200 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, status: value })}
                >
                    {Object.keys(statusColors).map((status) => (
                        <Select.Option key={status} value={status}>
                            {status}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            {/* ✅ Table */}
            <Table
                columns={columns}
                dataSource={filteredData.map((item, index) => ({
                    key: index,
                    ...item,
                }))}
                pagination={{ pageSize: 5 }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}